import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiUser } from "react-icons/fi";
import Get from "../services/Get";
import { useBinanceTickers } from "../services/BinanceTickerContext";
import { ThemeContext } from "./ThemeContext";
import "./DashboardHome.css";

const tabs = ["Holding", "Hot", "Top Gainers", "24h Volume"];
const DEFAULT_BALANCE_CURRENCIES = ["BTC", "ETH", "BNB", "USDT"];

const money = (value) => (value >= 1 ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value.toPrecision(4));
const STABLES = new Set(["USDT", "USDC", "FDUSD", "BUSD", "DAI"]);
const MIN_QUOTE_VOLUME = 1_000_000;
const compact = (value) => Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 2 }).format(value);
const discoverTabs = ["Earn", "Copy Trading"];

const SkeletonRows = ({ cols, count = 4 }) => Array.from({ length: count }, (_, row) => (
  <tr key={row} aria-hidden="true">
    {Array.from({ length: cols }, (_, col) => <td key={col}><span className="ud-shimmer" /></td>)}
  </tr>
));

const DashboardHome = () => {
  const { theme } = useContext(ThemeContext);
  const tickers = useBinanceTickers();
  const [tab, setTab] = useState("Holding");
  const [discoverTab, setDiscoverTab] = useState("Earn");
  const [balances, setBalances] = useState([]);
  const [balanceCurrency, setBalanceCurrency] = useState("BTC");
  const [plans, setPlans] = useState([]);
  const [vip, setVip] = useState(null);
  const [loaded, setLoaded] = useState({ wallets: false, plans: false });
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user")) || {}; } catch { return {}; }
  }, []);

  useEffect(() => {
    let active = true;
    const load = (request, apply, key) => request()
      .then((res) => { if (active) apply(res); })
      .catch(() => {})
      .finally(() => { if (active && key) setLoaded((prev) => ({ ...prev, [key]: true })); });
    load(() => Get.Wallets({ limit: 500 }), (res) => Array.isArray(res?.balances) && setBalances(res.balances), "wallets");
    load(Get.MonthlyRewardCatalog, (res) => Array.isArray(res?.data) && setPlans(res.data), "plans");
    load(Get.VipStatus, (res) => res?.data && setVip(res.data));
    return () => { active = false; };
  }, []);

  const priceOf = (symbol) => (STABLES.has(symbol) ? 1 : tickers?.[symbol]?.price || 0);

  const holdings = useMemo(() => balances
    .map((row) => ({
      symbol: row.asset?.symbol,
      locked: Number(row.reserved ?? 0) || 0,
      amount: (Number(row.balance ?? row.available ?? 0) || 0) + (Number(row.reserved ?? 0) || 0),
    }))
    .filter((row) => row.symbol && row.amount > 0)
    .map((row) => {
      const price = priceOf(row.symbol);
      return {
        ...row,
        price,
        valueUsd: row.amount * price,
        change: tickers?.[row.symbol]?.change ?? 0,
      };
    })
    .sort((left, right) => right.valueUsd - left.valueUsd),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [balances, tickers]);

  const totalUsd = holdings.reduce((sum, row) => sum + row.valueUsd, 0);
  const balanceCurrencies = useMemo(() => [...new Set([
    ...DEFAULT_BALANCE_CURRENCIES,
    ...Object.keys(tickers || {}),
    ...holdings.map((row) => row.symbol),
  ])].sort((left, right) => {
    const leftPriority = DEFAULT_BALANCE_CURRENCIES.indexOf(left);
    const rightPriority = DEFAULT_BALANCE_CURRENCIES.indexOf(right);
    if (leftPriority !== -1 || rightPriority !== -1) {
      return (leftPriority === -1 ? Number.MAX_SAFE_INTEGER : leftPriority)
        - (rightPriority === -1 ? Number.MAX_SAFE_INTEGER : rightPriority);
    }
    return left.localeCompare(right);
  }), [tickers, holdings]);
  const balanceCurrencyPrice = priceOf(balanceCurrency);
  const estimatedBalance = balanceCurrencyPrice ? totalUsd / balanceCurrencyPrice : 0;

  const rows = useMemo(() => {
    if (tab === "Holding") return holdings;
    const liquid = Object.entries(tickers || {})
      .map(([symbol, t]) => ({ symbol, ...t, quoteVolume: t.volume * t.price }))
      .filter((row) => !STABLES.has(row.symbol) && row.quoteVolume >= MIN_QUOTE_VOLUME);
    const sorters = {
      Hot: (a, b) => b.quoteVolume - a.quoteVolume,
      "Top Gainers": (a, b) => b.change - a.change,
      "24h Volume": (a, b) => b.quoteVolume - a.quoteVolume,
    };
    return liquid.sort(sorters[tab]).slice(0, 6);
  }, [tickers, tab, holdings]);

  const earnRows = useMemo(() => plans.flatMap((plan) => (plan.supported_assets || []).map((asset) => ({
    key: `${plan.plan_code}-${asset}`,
    asset,
    rate: Number(plan.monthly_rate || 0) * 100,
    duration: `${plan.duration_days} days`,
  }))), [plans]);

  return (
    <div className={`ud-home ${theme === "light" ? "ud-light" : ""}`}>
      <div className="ud-main">
        <div className="ud-profile">
          <div className="ud-avatar"><FiUser /></div>
          <div>
            <h2>{user.name || "User"}</h2>
            <div className="ud-meta">
              <span>UID {vip?.tillionx_id || user.tillionx_id || user.id || "-"}</span>
              <span>{user.email || ""}</span>
              <span className="ud-tag">{vip?.badge || "Regular User"}</span>
            </div>
          </div>
        </div>

        <section className="ud-card">
          <div className="ud-card-row">
            <div>
              <div className="ud-label">Est. Total Value</div>
              <div className="ud-value">
                {estimatedBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                <select
                  className="ud-balance-currency"
                  aria-label="Estimated balance currency"
                  value={balanceCurrency}
                  onChange={(event) => setBalanceCurrency(event.target.value)}
                >
                  {balanceCurrencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                </select>
              </div>
              <div className="ud-sub">≈ ${totalUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="ud-sub">{holdings.length} assets with balance</div>
            </div>
            <div className="ud-actions">
              <Link to="/depositCrypto" className="ud-btn ud-btn-primary">Deposit</Link>
              <Link to="/withdrawCrypto" className="ud-btn">Withdraw</Link>
              <Link to="/convert" className="ud-btn">Convert</Link>
            </div>
          </div>
        </section>

        <section className="ud-card">
          <div className="ud-card-head"><h3>Markets</h3><Link to="/markets/overview">More &gt;</Link></div>
          <div className="ud-tabs">
            {tabs.map((name) => (
              <button key={name} className={tab === name ? "active" : ""} onClick={() => setTab(name)}>{name}</button>
            ))}
          </div>
          <table className="ud-table">
            <thead><tr><th>Name</th>{tab === "Holding" ? <><th>Amount</th><th>Value (USD)</th></> : <th>24h Volume</th>}<th>Price</th><th>24h Change</th><th>Trade</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.symbol}>
                  <td>{row.symbol}</td>
                  {tab === "Holding"
                    ? <><td>{money(row.amount)}{row.locked > 0 && <small className="ud-locked" style={{ display: "block", opacity: 0.7 }}>🔒 Locked {money(row.locked)}</small>}</td><td>{row.price ? `$${money(row.valueUsd)}` : "--"}</td></>
                    : <td>${compact(row.quoteVolume)}</td>}
                  <td>{row.price ? `$${money(row.price)}` : "--"}</td>
                  <td className={row.change >= 0 ? "ud-up" : "ud-down"}>{row.price ? `${row.change >= 0 ? "+" : ""}${row.change.toFixed(2)}%` : "--"}</td>
                  <td><Link to="/markets/overview">Trade</Link></td>
                </tr>
              ))}
                            {rows.length === 0 && (tab === "Holding" ? loaded.wallets : false) && <tr><td colSpan="6" className="ud-empty">No holdings yet</td></tr>}
              {rows.length === 0 && !(tab === "Holding" && loaded.wallets) && <SkeletonRows cols={6} />}
            </tbody>
          </table>
        </section>

        <section className="ud-card">
          <div className="ud-card-head"><h3>Discover</h3><Link to="/simpleEarn">More &gt;</Link></div>
          <div className="ud-tabs">
            {discoverTabs.map((name) => (
              <button key={name} className={discoverTab === name ? "active" : ""} onClick={() => setDiscoverTab(name)}>{name}</button>
            ))}
          </div>
          {discoverTab === "Earn" ? (
            <table className="ud-table">
              <thead><tr><th>Coin</th><th>Monthly rate</th><th>Duration</th><th>Action</th></tr></thead>
              <tbody>
                {earnRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.asset}</td>
                    <td className="ud-up">{row.rate.toFixed(2)}%</td>
                    <td>{row.duration}</td>
                    <td><Link to="/simpleEarn">Subscribe</Link></td>
                  </tr>
                ))}
                {earnRows.length === 0 && loaded.plans && <tr><td colSpan="4" className="ud-empty">No earn plans available</td></tr>}
                {!loaded.plans && <SkeletonRows cols={4} />}
              </tbody>
            </table>
          ) : (
            <p className="ud-empty-block">Follow top traders and copy their trades. <Link to="/copy-trading/futures">Explore Copy Trading</Link></p>
          )}
        </section>
      </div>

      <aside className="ud-side">
        <section className="ud-card">
          <div className="ud-card-head"><h3>Announcements</h3><Link to="/announcement/announcement-all">More &gt;</Link></div>
          <ul className="ud-list">
            <li><Link to="/announcement/latest-activities">Latest activities</Link></li>
            <li><Link to="/announcement/new-fiat-listings">New listings</Link></li>
            <li><Link to="/announcement/maintenance-updates">Maintenance updates</Link></li>
          </ul>
        </section>
        <section className="ud-card ud-promo">
          <strong>EARN TOGETHER</strong>
          <p>Unlock up to 400 USDC every month by inviting friends.</p>
          <Link to="/referralDashboard" className="ud-btn ud-btn-primary">Invite Friends</Link>
        </section>
      </aside>
    </div>
  );
};

export default DashboardHome;
